import { useState, useRef, useCallback } from 'react';

interface UploadStepProps {
  onFileSelected: (file: File) => void;
  isParsing: boolean;
  parseError: string | null;
  onExport: () => void;
  isExporting: boolean;
  exportError: string | null;
  exportWarnings: string[];
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
  downloadTemplateError: string | null;
}

export const UploadStep: React.FC<UploadStepProps> = ({
  onFileSelected,
  isParsing,
  parseError,
  onExport,
  isExporting,
  exportError,
  exportWarnings,
  onDownloadTemplate,
  isDownloadingTemplate,
  downloadTemplateError,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      if (!file.name.endsWith('.xlsx')) return;
      setFileName(file.name);
      onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onClick={() => inputRef.current?.click()}
      >
        <p className="mb-2 text-lg font-medium text-gray-700">Excel ファイルをドラッグ＆ドロップ</p>
        <p className="mb-4 text-sm text-gray-500">またはクリックしてファイルを選択</p>
        <button
          type="button"
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          ファイルを選択
        </button>
        <p className="mt-3 text-xs text-gray-400">対応形式: .xlsx（FAQ シート＋症状シート）</p>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      {isParsing && (
        <div className="mt-4 text-center text-sm text-gray-500">ファイルを解析中...</div>
      )}

      {parseError && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{parseError}</div>
      )}

      {fileName && !isParsing && !parseError && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
          <span className="text-sm font-medium text-gray-700">{fileName}</span>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={onDownloadTemplate}
          disabled={isDownloadingTemplate}
          className="w-full rounded-lg border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDownloadingTemplate ? 'テンプレート生成中...' : 'Excel テンプレートをダウンロード'}
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={isExporting}
          className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isExporting ? 'エクスポート中...' : '現在のデータをエクスポート'}
        </button>
      </div>

      {exportError && (
        <div className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">{exportError}</div>
      )}

      {downloadTemplateError && (
        <div className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {downloadTemplateError}
        </div>
      )}

      {exportWarnings.length > 0 && (
        <div className="mt-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
          <p className="font-medium mb-1">データの整合性に関する警告:</p>
          <ul className="list-disc pl-5 space-y-1">
            {exportWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
